import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fmtDate } from "@/lib/format";
import { HelpCircle, Check, ChevronLeft, ChevronRight } from "lucide-react";
import { QtyInput } from "./EntryRow";

const Answers = ({ e, res, customers, onUpdate }) => {
  const nameIssue = ["suggested", "unmatched"].includes(res.match_type);
  const readIssue = (e.uncertain || e.overwritten) && !e.confirmed;
  const opts = [...new Set([e.tons_text, ...(e.alternatives || [])].filter(Boolean))];
  return (
    <div className="space-y-3">
      <div className="grid sm:grid-cols-3 gap-3">
        <div><div className="text-xs text-slate-500 mb-1">Date</div>
          <Input type="date" data-testid={`q-date-${e.id}`} className="h-8" value={e.date || ""} onChange={(v) => onUpdate({ ...e, date: v.target.value })} /></div>
        <div><div className="text-xs text-slate-500 mb-1">Name as written</div>
          <Input data-testid={`q-name-${e.id}`} className="h-8" value={e.customer_read} onChange={(v) => onUpdate({ ...e, customer_read: v.target.value, matched_customer: null, new_customer: false })} /></div>
        <div><div className="text-xs text-slate-500 mb-1">Quantity (use + for separate deliveries)</div><QtyInput entry={e} onChange={onUpdate} /></div>
      </div>
      {readIssue && (
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-sm text-slate-600">The correct quantity is:</span>
          {opts.map((o) => (
            <Button key={o} size="sm" variant="outline" data-testid={`q-pick-${e.id}-${o}`} onClick={() => onUpdate({ ...e, tons_text: o, tons: Number(o.split("+").reduce((a, p) => a + Number(p), 0).toFixed(3)), confirmed: true })}>{o}</Button>
          ))}
          <Button size="sm" data-testid={`q-confirm-${e.id}`} className="bg-emerald-600 hover:bg-emerald-700" disabled={e.tons == null || !e.date} onClick={() => onUpdate({ ...e, confirmed: true })}>
            <Check className="h-3 w-3 mr-1" />Confirm values above
          </Button>
        </div>
      )}
      {nameIssue && (
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-sm text-slate-600">Which website customer is this?</span>
          {res.suggestion && <Button size="sm" variant="outline" data-testid={`q-accept-name-${e.id}`} onClick={() => onUpdate({ ...e, matched_customer: res.suggestion })}>Yes, {res.suggestion}</Button>}
          <select data-testid={`q-choose-name-${e.id}`} className="h-8 rounded-md border text-sm px-1" value="" onChange={(v) => v.target.value && onUpdate({ ...e, matched_customer: v.target.value })}>
            <option value="">Other customer…</option>{customers.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <Button size="sm" variant="ghost" data-testid={`q-new-name-${e.id}`} onClick={() => onUpdate({ ...e, new_customer: true })}>Not on website (keep as written)</Button>
        </div>
      )}
    </div>
  );
};

export const QuestionsPanel = ({ entries, resolution, customers, onUpdate, onDelete }) => {
  const open = entries.filter((e) => resolution[e.id]?.issues?.length);
  const [i, setI] = useState(0);
  if (!open.length) return null;
  const idx = Math.min(i, open.length - 1);
  const e = open[idx];
  const res = resolution[e.id];
  return (
    <section data-testid="questions-panel" className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-chivo font-black text-lg flex items-center gap-2 text-amber-900"><HelpCircle className="h-5 w-5" />Questions for you ({open.length} left)</h3>
        <div className="flex items-center gap-1">
          <Button size="icon" variant="outline" data-testid="q-prev" className="h-8 w-8" disabled={idx === 0} onClick={() => setI(idx - 1)}><ChevronLeft className="h-4 w-4" /></Button>
          <span className="text-xs text-amber-900 px-1">{idx + 1}/{open.length}</span>
          <Button size="icon" variant="outline" data-testid="q-next" className="h-8 w-8" disabled={idx >= open.length - 1} onClick={() => setI(idx + 1)}><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>
      <div data-testid={`question-${e.id}`} className="bg-white rounded-lg border p-3 space-y-3">
        <div className="text-sm"><b>{e.plant}</b> · page {e.page ?? "manual"} · {e.date ? fmtDate(e.date) : "date unclear"} · <b>{e.customer_read || "?"}</b> — {e.tons_text || "?"}</div>
        <ul className="text-sm text-amber-800 list-disc pl-5">{res.issues.map((x) => <li key={x}>{x}</li>)}</ul>
        {e.note && <p className="text-xs text-slate-500">AI note: {e.note}</p>}
        {e.crop ? <img data-testid={`q-crop-${e.id}`} src={e.crop} alt="Handwritten line" className="max-w-full max-h-48 rounded border" />
          : <p className="text-xs text-slate-400">No picture available for this line.</p>}
        <Answers e={e} res={res} customers={customers} onUpdate={onUpdate} />
        <button data-testid={`q-delete-${e.id}`} className="text-xs text-red-600 hover:underline" onClick={() => onDelete(e.id)}>This is not a sale line — remove it</button>
      </div>
    </section>
  );
};
